import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, Sparkles } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "../components/CtaBand";
import { Seo, breadcrumbJsonLd } from "../components/Seo";
import { T, paths } from "../i18n";
import { ARTICLES } from "../data/articles";
import { fetchDbArticles } from "../api";

export default function GuidesIndex({ lang }) {
  const t = T[lang];
  const p = paths(lang);
  const [dbArticles, setDbArticles] = useState([]);

  useEffect(() => {
    fetchDbArticles().then(setDbArticles);
  }, []);

  const seen = new Set();
  const articles = [...dbArticles, ...ARTICLES]
    .filter((a) => (seen.has(a.slug) ? false : (seen.add(a.slug), true)))
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  const title = lang === "fr"
    ? "Guides de sécurité au Québec — Conseils d'experts | 1st Nations Security"
    : "Quebec Security Guides — Expert Advice | 1st Nations Security";
  const desc = lang === "fr"
    ? "Guides pratiques sur la sécurité au Québec : permis BSP, surveillance incendie, gardiennage de chantier, patrouille mobile, sécurité événementielle et plus, par 1st Nations Security."
    : "Practical security guides for Quebec: BSP licensing, fire watch rules, construction site theft prevention, mobile patrol vs static guards, event security planning and more.";

  return (
    <div data-testid="guides-index-page">
      <Seo lang={lang} title={title} description={desc} enPath="/guides" frPath="/fr/guides"
        jsonLd={breadcrumbJsonLd([[t.breadcrumbHome, p.home], [t.nav.guides, p.guides]])} />
      <PageHeader lang={lang} eyebrow={t.brandTag}
        title={lang === "fr" ? "Guides et conseils en sécurité" : "Security Guides & Insights"}
        breadcrumbs={[[t.breadcrumbHome, p.home], [t.nav.guides, null]]} />

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <Reveal>
          <p className="max-w-2xl text-base text-slate-400">
            {lang === "fr"
              ? "Des réponses claires aux questions que les gestionnaires québécois posent vraiment — deux nouveaux guides chaque mois."
              : "Clear answers to the questions Quebec property managers actually ask — two fresh guides published every month."}
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {articles.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 2) * 0.08}>
              <Link to={p.guide(a.slug)} className="group flex h-full flex-col rounded-2xl border border-slate-800/70 bg-card/60 p-7 transition-all hover:-translate-y-1 hover:border-amber-500/40 hover:shadow-[0_16px_40px_rgba(245,124,0,0.12)]" data-testid={`guide-card-${a.slug}`}>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">{a.category[lang]}</span>
                  {a.source === "auto" && (
                    <span className="flex items-center gap-1 rounded-full border border-slate-700 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      <Sparkles size={10} className="text-amber-400" /> {lang === "fr" ? "Nouveau" : "Fresh"}
                    </span>
                  )}
                </div>
                <h2 className="mt-4 font-display text-xl font-bold leading-snug text-slate-100 group-hover:text-amber-300">{a[lang].title}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{a[lang].excerpt}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="flex items-center gap-4 font-mono text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><CalendarDays size={12} /> {a.date}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {a.readMin} min</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-400">
                    {lang === "fr" ? "Lire" : "Read"} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
      <CtaBand lang={lang} />
    </div>
  );
}
